/**
 * UI Renderer
 *
 * Renders HUD elements (score, combo, judgment text, etc.)
 */

export class UIRenderer {
  private currentJudgment: string | null = null;
  private judgmentDisplayTime: number = 0;
  private readonly JUDGMENT_DURATION: number = 300; // Show judgment for 300ms

  /**
   * Render UI elements
   */
  render(ctx: CanvasRenderingContext2D, score: number, combo: number): void {
    this.renderScore(ctx, score);
    this.renderCombo(ctx, combo);
    this.renderJudgment(ctx);
  }

  /**
   * Render score display
   */
  private renderScore(ctx: CanvasRenderingContext2D, score: number): void {
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 5;
    ctx.shadowColor = '#000000';

    ctx.fillText(`Score: ${score.toLocaleString()}`, 20, 40);

    ctx.shadowBlur = 0;
  }

  /**
   * Render combo counter
   */
  private renderCombo(ctx: CanvasRenderingContext2D, combo: number): void {
    if (combo === 0) return;

    const canvas = ctx.canvas;
    const x = canvas.width - 20;

    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'right';
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 5;
    ctx.shadowColor = '#000000';

    ctx.fillText(`Combo: ${combo}`, x, 40);

    // Larger combo display if combo is high
    if (combo >= 50) {
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#ffd700';
      ctx.textAlign = 'center';
      ctx.fillText(`${combo}`, canvas.width / 2, 100);
    }

    ctx.shadowBlur = 0;
  }

  /**
   * Render judgment text (PERFECT, GREAT, GOOD, MISS)
   */
  private renderJudgment(ctx: CanvasRenderingContext2D): void {
    if (!this.currentJudgment) return;

    const now = performance.now();
    if (now - this.judgmentDisplayTime > this.JUDGMENT_DURATION) {
      this.currentJudgment = null;
      return;
    }

    const canvas = ctx.canvas;
    const x = canvas.width / 2;
    const y = canvas.height * 0.8 + 80; // Below judgment line

    const colors: { [key: string]: string } = {
      'PERFECT': '#ffd700',
      'GREAT': '#00ff88',
      'GOOD': '#4ecdc4',
      'MISS': '#ff6b6b'
    };

    ctx.font = 'bold 36px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = colors[this.currentJudgment] || '#ffffff';
    ctx.shadowBlur = 10;
    ctx.shadowColor = colors[this.currentJudgment] || '#ffffff';

    ctx.fillText(this.currentJudgment, x, y);

    ctx.shadowBlur = 0;
  }

  /**
   * Show a judgment (called when note is hit)
   */
  showJudgment(judgment: string): void {
    this.currentJudgment = judgment;
    this.judgmentDisplayTime = performance.now();
  }

  /**
   * Render accuracy percentage
   */
  renderAccuracy(ctx: CanvasRenderingContext2D, accuracy: number): void {
    ctx.font = '18px Arial';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';

    ctx.fillText(`Accuracy: ${accuracy.toFixed(2)}%`, 20, 70);
  }
}
